import { useState, useRef, useEffect } from "react";
import { Mic, Square, Play, Pause } from "lucide-react";
import { Button } from "@/components/ui/button";

export function VoiceRecorder({ onSend, disabled }) {
  const [recording, setRecording] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const start = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, { mimeType: "audio/webm" });
      chunksRef.current = [];
      mr.ondataavailable = (e) => e.data.size > 0 && chunksRef.current.push(e.data);
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        stream.getTracks().forEach((t) => t.stop());
        await onSend(blob);
      };
      mediaRef.current = mr;
      mr.start();
      setRecording(true);
      setElapsed(0);
      timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
    } catch (e) {
      alert("Не удалось получить доступ к микрофону");
    }
  };

  const stop = () => {
    if (mediaRef.current && recording) {
      mediaRef.current.stop();
      setRecording(false);
      clearInterval(timerRef.current);
    }
  };

  useEffect(() => () => timerRef.current && clearInterval(timerRef.current), []);

  return (
    <div className="flex items-center gap-3">
      {!recording ? (
        <Button
          data-testid="voice-record-button"
          type="button"
          onClick={start}
          disabled={disabled}
          size="icon"
          variant="outline"
          className="bg-[#131924] border-[#212b3d] hover:bg-[#1c2434] hover:text-amber-400"
          title="Записать голосовое"
        >
          <Mic size={18} />
        </Button>
      ) : (
        <>
          <Button
            data-testid="voice-stop-button"
            type="button"
            onClick={stop}
            size="icon"
            className="bg-red-500 hover:bg-red-600 text-white"
          >
            <Square size={16} />
          </Button>
          <div className="flex items-center gap-1.5 text-red-300 text-sm font-mono">
            <span className="wave-bar" style={{ animationDelay: "0s" }} />
            <span className="wave-bar" style={{ animationDelay: "0.15s" }} />
            <span className="wave-bar" style={{ animationDelay: "0.3s" }} />
            <span className="wave-bar" style={{ animationDelay: "0.45s" }} />
            <span className="ml-2">{String(Math.floor(elapsed / 60)).padStart(2, "0")}:{String(elapsed % 60).padStart(2, "0")}</span>
          </div>
        </>
      )}
    </div>
  );
}

export function VoicePlayer({ src, transcript }) {
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) audioRef.current.pause();
    else audioRef.current.play();
  };

  return (
    <div className="rounded-lg border border-[#212b3d] bg-[#0f1521] p-3 max-w-md">
      <div className="flex items-center gap-3">
        <button
          data-testid="voice-play-button"
          type="button"
          onClick={toggle}
          className="w-10 h-10 rounded-full bg-amber-500 hover:bg-amber-600 text-slate-900 flex items-center justify-center transition"
        >
          {playing ? <Pause size={18} /> : <Play size={18} className="ml-0.5" />}
        </button>
        <div className="flex-1">
          <div className="h-1.5 rounded-full bg-[#1c2434] overflow-hidden">
            <div
              className="h-full bg-amber-500 transition-all"
              style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }}
            />
          </div>
          <div className="text-[11px] text-slate-500 font-mono mt-1">
            {formatT(progress)} / {formatT(duration)}
          </div>
        </div>
      </div>
      <audio
        ref={audioRef}
        src={src}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onLoadedMetadata={(e) => {
          const d = e.currentTarget.duration;
          setDuration(isFinite(d) ? d : 0);
        }}
        onTimeUpdate={(e) => setProgress(e.currentTarget.currentTime)}
      />
      {transcript && (
        <div className="mt-3 pt-3 border-t border-[#212b3d]">
          <div className="text-[10px] uppercase tracking-widest text-amber-400/80 font-medium mb-1">
            Whisper AI Транскрипция
          </div>
          <div className="text-sm text-slate-300 italic leading-relaxed">"{transcript}"</div>
        </div>
      )}
    </div>
  );
}

function formatT(s) {
  if (!s || !isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  const sec = Math.floor(s % 60);
  return `${m}:${String(sec).padStart(2, "0")}`;
}
